
import React from 'react';
import { Button } from '@/components/ui/button';
import { Plus, Filter } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useIsMobile } from '@/hooks/use-mobile';

interface LeadsHeaderProps {
  totalLeads: number;
  counties: string[];
  onFilterChange: (filterType: 'status' | 'county', value: string | null) => void;
  filters: {
    status: string | null;
    county: string | null;
  };
}

export function LeadsHeader({ totalLeads, counties, onFilterChange, filters }: LeadsHeaderProps) {
  const isMobile = useIsMobile();
  
  return (
    <div className="flex flex-col space-y-4 md:space-y-0 md:flex-row md:items-center md:justify-between">
      <div>
        <h2 className="text-xl md:text-2xl font-semibold flex items-center">
          Nursing Home Leads
        </h2>
        <p className="text-sm text-gray-600 mt-1">
          {totalLeads} nursing {totalLeads === 1 ? 'home' : 'homes'} in your list
        </p>
      </div>
      
      <div className="flex flex-col space-y-3 sm:flex-row sm:space-y-0 sm:space-x-3 sm:items-center">
        <div className="flex space-x-2 items-center">
          {!isMobile && <Filter size={16} className="text-gray-500" />}
          
          <Select 
            value={filters.status || 'all'} 
            onValueChange={value => onFilterChange('status', value === 'all' ? null : value)}
          >
            <SelectTrigger className={`w-full sm:w-[130px] ${isMobile ? 'h-9' : ''}`}>
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="interested">Interested</SelectItem>
                <SelectItem value="to_visit">To Visit</SelectItem>
                <SelectItem value="visit_scheduled">Visit Scheduled</SelectItem>
                <SelectItem value="visited">Visited</SelectItem>
                <SelectItem value="shortlisted">Shortlisted</SelectItem>
                <SelectItem value="application_submitted">Application Submitted</SelectItem>
                <SelectItem value="waitlist">On Waitlist</SelectItem>
                <SelectItem value="declined">Declined</SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
          
          <Select 
            value={filters.county || 'all'} 
            onValueChange={value => onFilterChange('county', value === 'all' ? null : value)}
          >
            <SelectTrigger className={`w-full sm:w-[130px] ${isMobile ? 'h-9' : ''}`}>
              <SelectValue placeholder="County" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="all">All Counties</SelectItem>
                {counties.map(county => (
                  <SelectItem key={county} value={county}>
                    {county}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
        
        <Button className="w-full sm:w-auto" asChild>
          <a href="/nursing-homes">
            <Plus className="mr-1 h-4 w-4" />
            {isMobile ? 'Add' : 'Add More Homes'}
          </a>
        </Button>
      </div>
    </div>
  );
}
